import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { Loader2, LogOut, Trash2, Mail, Phone, Calendar as Cal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lilah_admin_token";

const STATUS = ["pending", "confirmed", "completed"];
const statusColor = {
  pending: "bg-[#E8B059]/20 text-[#8a6300] border-[#E8B059]/40",
  confirmed: "bg-[#E2725B]/15 text-[#a8402c] border-[#E2725B]/40",
  completed: "bg-[#2D2A26]/10 text-[#2D2A26]/70 border-[#2D2A26]/20",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [authing, setAuthing] = useState(false);
  const [unavailable, setUnavailable] = useState([]);
  const [newDate, setNewDate] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); }
      else toast.error("Failed to load bookings");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const loadUnavail = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/unavailable-dates`); setUnavailable(data.dates || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { if (token) loadUnavail(); }, [token, loadUnavail]);

  const addDate = async () => {
    if (!newDate) return;
    try {
      await axios.post(`${API}/admin/unavailable-dates`, { date: newDate }, auth);
      setUnavailable((u) => [...new Set([...u, newDate])].sort());
      setNewDate("");
      toast.success("Date blocked");
    } catch { toast.error("Could not block date"); }
  };

  const removeDate = async (d) => {
    try {
      await axios.delete(`${API}/admin/unavailable-dates/${d}`, auth);
      setUnavailable((u) => u.filter((x) => x !== d));
    } catch { toast.error("Could not unblock date"); }
  };

  const login = async (e) => {
    e.preventDefault();
    setAuthing(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setAuthing(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setBookings([]); };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, auth);
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, auth);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Booking removed");
    } catch { toast.error("Delete failed"); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A181C] px-6">
        <Toaster position="bottom-right" />
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-[#F9F6F0] rounded-sm p-10">
          <p className="overline text-[#E2725B] mb-3">Private</p>
          <h1 className="font-serif-display text-4xl font-light mb-8">Studio Login</h1>
          <input data-testid="admin-email" type="email" required placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            className="w-full bg-transparent border-b border-[#2D2A26]/25 py-3 mb-6 focus:border-[#E2725B] focus:outline-none" />
          <input data-testid="admin-password" type="password" required placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            className="w-full bg-transparent border-b border-[#2D2A26]/25 py-3 mb-8 focus:border-[#E2725B] focus:outline-none" />
          <button data-testid="admin-login-btn" disabled={authing} className="w-full py-4 bg-[#2D2A26] text-[#F9F6F0] overline text-[0.66rem] rounded-full hover:bg-[#E2725B] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {authing && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>
        </form>
      </div>
    );
  }

  const pending = bookings.filter((b) => b.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#F9F6F0] px-6 md:px-12 py-12" data-testid="admin-dashboard">
      <Toaster position="bottom-right" />
      <header className="flex items-center justify-between mb-12 border-b border-[#2D2A26]/15 pb-6">
        <div>
          <p className="overline text-[#E2725B] mb-2">Studio · Admin</p>
          <h1 className="font-serif-display text-4xl md:text-5xl font-light">Booking requests</h1>
          <p className="text-[#2D2A26]/60 text-sm mt-2">{bookings.length} total · {pending} pending</p>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 overline text-[0.62rem] border border-[#2D2A26]/25 px-5 py-2.5 rounded-full hover:bg-[#2D2A26] hover:text-[#F9F6F0] transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </header>

      <section className="mb-14 bg-white border border-[#2D2A26]/10 rounded-sm p-6 md:p-8" data-testid="availability-manager">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-5">
          <div>
            <p className="overline text-[#E2725B] mb-1">Availability</p>
            <h2 className="font-serif-display text-2xl md:text-3xl font-light">Blocked dates</h2>
            <p className="text-[#2D2A26]/55 text-sm mt-1">Clients can't request these days.</p>
          </div>
          <div className="flex items-center gap-3">
            <input type="date" data-testid="block-date-input" value={newDate} min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setNewDate(e.target.value)}
              className="border border-[#2D2A26]/20 rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#E2725B]" />
            <button data-testid="block-date-btn" onClick={addDate}
              className="overline text-[0.6rem] bg-[#2D2A26] text-[#F9F6F0] px-6 py-3 rounded-full hover:bg-[#E2725B] transition-colors">Block</button>
          </div>
        </div>
        {unavailable.length === 0 ? (
          <p className="text-[#2D2A26]/45 text-sm">No blocked dates yet — every future date is open.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="blocked-dates-list">
            {unavailable.map((d) => (
              <span key={d} data-testid={`blocked-${d}`} className="inline-flex items-center gap-2 bg-[#F1EBE1] border border-[#2D2A26]/10 rounded-full pl-4 pr-2 py-1.5 text-sm">
                {d}
                <button data-testid={`unblock-${d}`} onClick={() => removeDate(d)}
                  className="w-5 h-5 flex items-center justify-center rounded-full text-[#2D2A26]/60 hover:bg-[#E2725B] hover:text-white transition-colors">×</button>
              </span>
            ))}
          </div>
        )}
      </section>

      {loading ? (
        <div className="flex items-center gap-3 text-[#2D2A26]/60"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : bookings.length === 0 ? (
        <p className="font-serif-display text-3xl italic text-[#2D2A26]/40 py-20">No requests yet.</p>
      ) : (
        <div className="grid gap-5">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-white border border-[#2D2A26]/10 rounded-sm p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="md:w-64 shrink-0">
                <p className="font-serif-display text-2xl">{b.name}</p>
                <p className="overline text-[0.55rem] text-[#E2725B] mt-1">{b.service}</p>
              </div>
              <div className="flex-1 grid sm:grid-cols-3 gap-3 text-sm text-[#2D2A26]/70">
                <span className="flex items-center gap-2"><Cal className="w-4 h-4 text-[#E2725B]" strokeWidth={1.5} /> {b.date}</span>
                <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#E2725B]"><Mail className="w-4 h-4 text-[#E2725B]" strokeWidth={1.5} /> {b.email}</a>
                <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#E2725B]" strokeWidth={1.5} /> {b.phone || "—"}</span>
                {b.message && <p className="sm:col-span-3 text-[#2D2A26]/60 italic">"{b.message}"</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <select data-testid={`status-select-${b.id}`} value={b.status} onChange={(e) => setStatus(b.id, e.target.value)}
                  className={`text-xs px-3 py-2 rounded-full border capitalize outline-none cursor-pointer ${statusColor[b.status]}`}>
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`delete-${b.id}`} onClick={() => remove(b.id)} className="w-9 h-9 flex items-center justify-center rounded-full border border-[#2D2A26]/20 hover:bg-[#E2725B] hover:text-white hover:border-[#E2725B] transition-colors">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
